/**
 * ⌘K / Ctrl-K page finder. Lists every page the signed-in person can reach
 * (the same filtered nav tree the dock shows), fuzzy-ish matched on label and
 * section, and navigates on Enter. Rendered through a portal so no ancestor
 * transform or blur can trap it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Search } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { NavItem } from "./nav";
import { cn } from "@/lib/utils";

interface Group { label: string | null; icon?: LucideIcon; items: NavItem[] }

interface Entry { label: string; to: string; trail: string; icon?: LucideIcon }

function flatten(groups: Group[]): Entry[] {
  const out: Entry[] = [];
  for (const g of groups) {
    for (const item of g.items) {
      if (item.to) out.push({ label: item.label, to: item.to, trail: g.label ?? "", icon: item.icon ?? g.icon });
      for (const c of item.children ?? []) {
        if (c.to) out.push({ label: c.label, to: c.to, trail: [g.label, item.label].filter(Boolean).join(" / "), icon: item.icon ?? g.icon });
      }
    }
  }
  return out;
}

/** Every query word must appear somewhere in label or trail; label hits rank first. */
function score(e: Entry, q: string): number {
  if (!q) return 1;
  const label = e.label.toLowerCase();
  const all = `${label} ${e.trail.toLowerCase()}`;
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.every((w) => all.includes(w))) return 0;
  let s = 1;
  if (label.startsWith(words[0])) s += 4;
  for (const w of words) if (label.includes(w)) s += 2;
  return s;
}

export function CommandPalette({ open, onClose, groups }: { open: boolean; onClose: () => void; groups: Group[] }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const entries = useMemo(() => flatten(groups), [groups]);
  const results = useMemo(
    () => entries.map((e) => ({ e, s: score(e, q) })).filter((r) => r.s > 0).sort((a, b) => b.s - a.s).slice(0, 40).map((r) => r.e),
    [entries, q],
  );

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-i="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  if (!open) return null;

  const go = (e?: Entry) => {
    if (!e) return;
    onClose();
    navigate(e.to);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-3 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Jump to a page">
      <div className="absolute inset-0 bg-[#05080B]/55 backdrop-blur-[3px] animate-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-[560px] animate-slide-up overflow-hidden rounded-2xl border border-border bg-card shadow-overlay">
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="h-[18px] w-[18px] shrink-0 text-fg-subtle" strokeWidth={1.8} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(results.length - 1, i + 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
              else if (e.key === "Enter") { e.preventDefault(); go(results[idx]); }
              else if (e.key === "Escape") { e.preventDefault(); onClose(); }
            }}
            placeholder="Search pages — deposits, KYC, IB tree…"
            className="h-14 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-fg-subtle"
            aria-label="Search pages"
          />
          <kbd className="rounded-md border border-border bg-bg-elevated px-1.5 py-0.5 text-[10.5px] font-semibold text-fg-muted">Esc</kbd>
        </div>
        <ul ref={listRef} className="max-h-[52vh] overflow-y-auto p-2" role="listbox">
          {results.length === 0 && (
            <li className="px-3 py-10 text-center text-[13px] text-fg-subtle">No page matches “{q}”.</li>
          )}
          {results.map((e, i) => {
            const Icon = e.icon;
            return (
              <li key={e.to + e.label} data-i={i} role="option" aria-selected={i === idx}>
                <button
                  onMouseMove={() => setIdx(i)}
                  onClick={() => go(e)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-control px-3 py-2.5 text-left transition-colors duration-feedback",
                    i === idx ? "bg-primary-soft" : "hover:bg-bg-elevated",
                  )}
                >
                  <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border", i === idx ? "text-primary" : "text-fg-subtle")}>
                    {Icon && <Icon className="h-4 w-4" strokeWidth={1.7} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-fg">{e.label}</span>
                    {e.trail && <span className="block truncate text-[11.5px] text-fg-subtle">{e.trail}</span>}
                  </span>
                  {i === idx && <CornerDownLeft className="h-4 w-4 text-fg-subtle" strokeWidth={1.7} />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
